import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsEventsService } from '@techspert-io/analytics';
import { BannerService } from '@techspert-io/growthbook';
import { OpportunityService } from '@techspert-io/opportunities';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  filter,
  map,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import {
  IDataCaptureSegment,
  IDataCaptureSheet,
  IDiscussionGuideQuestion,
} from '../../models/data-capture.models';
import { DiscussionGuideUploadJobStatus } from '../../models/discussion-guide-upload-job.models';
import {
  DataCaptureSheetService,
  IDataCaptureSheetDefaults,
} from '../../services/data-capture-sheet.service';
import { DataCaptureService } from '../../services/data-capture.service';
import { QuestionHierarchyService } from '../../services/question-hierarchy.service';
import { ThemeValidationService } from '../../services/theme-validator.service';
import {
  sheetNameValidator,
  trimmedLengthValidator,
  trimSheetName,
} from '../../validators/sheet-name.validators';
import { FormGroupQuestion } from '../re-orderable-theme/question-node/question-node.component';
import { FormGroupTheme } from '../re-orderable-theme/re-orderable-theme.component';

@Component({
  selector: 'app-sheet-get-started',
  templateUrl: './sheet-get-started.component.html',
  styleUrls: ['./sheet-get-started.component.scss'],
  standalone: false,
})
export class SheetGetStartedComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  sheetId = crypto.randomUUID();

  sheetNames: string[] = [];
  lastStepIndex = 0;
  themesFormSubmitted = false;
  collapsedPreview = true;

  sheetDefaults: Partial<IDataCaptureSheetDefaults> = {};

  gettingStartedFormGroup = new FormGroup({
    sheetName: new FormControl('', {
      validators: [
        trimmedLengthValidator(),
        sheetNameValidator(() => this.sheetNames),
      ],
      nonNullable: true,
    }),
  });

  segmentsFormGroup = new FormGroup({
    segments: new FormControl([], [Validators.required, Validators.min(1)]),
  });

  themesFormGroup = new FormGroup({
    reorderablePanel: new FormGroup({
      themes: new FormArray<FormGroupTheme>(
        [
          new FormGroup({
            theme: new FormControl<string>(
              'Interview section',
              Validators.required
            ),
            questions: new FormArray<FormGroupQuestion>([
              new FormGroup({
                question_id: new FormControl<string>(crypto.randomUUID()),
                question: new FormControl<string>('', Validators.required),
                theme: new FormControl<string>(
                  'Interview section',
                  Validators.required
                ),
                label: new FormControl<string>(''),
                parent_question_id: new FormControl<string | null>(null),
                children: new FormArray<FormGroupQuestion>([]),
              }),
            ]),
          }),
        ],
        [Validators.required, this.themeValidatorService.validate()]
      ),
    }),
  });

  baseDiscussionGuideInput: 'file' | 'manual';
  discussionGuideFile = new FormControl<File>(null);

  answerConfigFormGroup = new FormGroup({
    answerType: new FormControl<'summarised' | 'verbatim'>(
      'summarised',
      Validators.required
    ),
  });

  additionalFieldsFormGroup = new FormGroup({
    expertName: new FormControl(true),
    dateAndTimeOfCall: new FormControl(true),
    geography: new FormControl(true),
    profileType: new FormControl(true),
    interviewerName: new FormControl(false),
    notetakerName: new FormControl(false),
    recruitmentSource: new FormControl(false),
    expertBio: new FormControl(false),
    suggestedQuestionThreshold: new FormControl<number>(2, {
      nonNullable: true,
      validators: [Validators.min(2)],
    }),
  });

  segments: IDataCaptureSegment[] = [];

  loading = true;

  creatingSheet = false;

  themeFormChange$: Observable<boolean> = EMPTY;

  opportunityName: string;
  opportunityId: string;
  publicDisplayId: string;
  processingDiscussionGuide: boolean;

  get sheetName(): FormControl<string> {
    return this.gettingStartedFormGroup.controls.sheetName;
  }

  get themesControl(): FormArray<FormGroupTheme> {
    return this.themesFormGroup.controls.reorderablePanel.controls.themes;
  }

  get sheetHeaders(): IDataCaptureSheet['additional_export_headers'] {
    const additionalFields = this.additionalFieldsFormGroup.value;

    return {
      expert_name: additionalFields.expertName,
      expert_bio: additionalFields.expertBio,
      date_and_time_of_call: additionalFields.dateAndTimeOfCall,
      geography: additionalFields.geography,
      profile_type: additionalFields.profileType,
      interviewer_name: additionalFields.interviewerName,
      notetaker_name: additionalFields.notetakerName,
      respondent_source: additionalFields.recruitmentSource,
    };
  }

  get selectedSegments() {
    return this.segments.filter((segment) =>
      this.segmentsFormGroup.controls.segments.value.includes(segment.segmentId)
    );
  }

  get themes() {
    return this.questionHierarchyService.flattenThemes(this.themesControl);
  }

  get stepperHeight() {
    return `calc(100vh - 88px - ${this.bannerService.banner ? 48 : 0}px)`;
  }

  constructor(
    private dataCaptureService: DataCaptureService,
    private dataCaptureSheetService: DataCaptureSheetService,
    private questionHierarchyService: QuestionHierarchyService,
    private opportunityService: OpportunityService,
    private analyticsEventsService: AnalyticsEventsService,
    private bannerService: BannerService,
    private toastService: ToastrService,
    private activatedRoute: ActivatedRoute,
    private themeValidatorService: ThemeValidationService,
    private router: Router
  ) {}

  ngOnInit() {
    this.sheetDefaults = this.dataCaptureSheetService.sheetDefaults;
    this.dataCaptureSheetService.sheetDefaults = {};

    this.themeFormChange$ = this.themesFormGroup.valueChanges.pipe(
      debounceTime(200),
      map(() => true)
    );

    combineLatest([this.activatedRoute.paramMap])
      .pipe(
        map(([params]) => params.get('publicDisplayId')),
        switchMap((publicDisplayId) =>
          this.opportunityService
            .getOpportunityByDisplayId(publicDisplayId)
            .pipe(map((opportunity) => ({ opportunity })))
        ),
        switchMap(({ opportunity }) =>
          combineLatest([
            this.dataCaptureService.getAvailableSegments(
              opportunity.opportunityId
            ),
            this.dataCaptureService.querySheetsByOpportunityId(
              opportunity.opportunityId
            ),
          ]).pipe(
            map(([availableSegments, sheets]) => ({
              availableSegments,
              sheets,
              opportunity,
            }))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ availableSegments, sheets, opportunity }) => {
        this.segments = availableSegments;
        this.sheetNames = sheets.map((sheet) => sheet.name);
        this.opportunityId = opportunity.opportunityId;
        this.publicDisplayId = opportunity.publicDisplayId;
        this.opportunityName = opportunity.opportunityName;

        this.setDefaults();

        this.analyticsEventsService.sendEvent('setup', {
          event_category: 'data_capture',
          event_label: 'get_started',
          value: opportunity.opportunityId,
          groupId: this.sheetId,
        });

        this.analyticsEventsService.sendEvent('setup', {
          event_category: 'data_capture',
          event_label: 'step_time',
          value: 'get_started',
          groupId: this.sheetId,
        });

        this.loading = false;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectionChange(currentStepIndex: number) {
    if (this.lastStepIndex !== currentStepIndex) {
      const stepMap = {
        0: 'get_started',
        1: 'segments',
        2: 'themes',
        3: 'answer_config',
        4: 'additional_fields',
        5: 'preview',
      };

      this.analyticsEventsService.sendEvent('setup', {
        event_category: 'data_capture',
        event_label: 'step_time',
        value: stepMap[currentStepIndex],
        groupId: this.sheetId,
      });

      this.themesFormSubmitted = false;
    }

    this.lastStepIndex = currentStepIndex;
  }

  complete() {
    const { sheetName } = this.gettingStartedFormGroup.value;
    const { segments } = this.segmentsFormGroup.value;
    const additionalFields = this.additionalFieldsFormGroup.value;

    this.creatingSheet = true;

    this.dataCaptureService
      .createSheet({
        data_capture_sheet_id: this.sheetId,
        name: trimSheetName(sheetName),
        opportunity_id: this.opportunityId,
        opportunity_segment_ids: segments,
        discussion_guide: this.themes.map((q) => ({
          ...q,
          question: q.question.trim(),
        })),
        answer_format: this.answerConfigFormGroup.value.answerType,
        suggested_question_threshold:
          additionalFields.suggestedQuestionThreshold,
        additional_export_headers: {
          expert_name: additionalFields.expertName,
          expert_bio: additionalFields.expertBio,
          date_and_time_of_call: additionalFields.dateAndTimeOfCall,
          geography: additionalFields.geography,
          profile_type: additionalFields.profileType,
          interviewer_name: additionalFields.interviewerName,
          notetaker_name: additionalFields.notetakerName,
          respondent_source: additionalFields.recruitmentSource,
        },
      })
      .pipe(
        switchMap(() =>
          this.router.navigate(['/connect', this.publicDisplayId], {
            queryParams: {
              tab: 'dataCapture',
            },
          })
        ),
        catchError((err) => {
          this.creatingSheet = false;

          this.toastService.error('Please try again', 'Error creating sheet');

          return err;
        })
      )
      .subscribe();
  }

  cancelCreation() {
    this.router.navigate(['/connect', this.publicDisplayId], {
      queryParams: {
        tab: 'dataCapture',
      },
    });
  }

  onThemesFormSubmit() {
    this.themesFormSubmitted = true;
    this.themesFormGroup.markAllAsTouched();
  }

  setCollapsedState() {
    this.collapsedPreview = !this.collapsedPreview;
  }

  onDiscussionGuideDocxChange(target: HTMLInputElement) {
    const file = Array.from(target?.files || []).find(Boolean);

    if (file) {
      const validMimeTypes = [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];

      const validExtensions = ['.docx'];
      const fileExtension = file.name
        .toLowerCase()
        .slice(file.name.lastIndexOf('.'));

      const isValidMimeType = validMimeTypes.includes(file.type);
      const isValidExtension = validExtensions.includes(fileExtension);

      if (!isValidMimeType || !isValidExtension) {
        this.discussionGuideFile.setErrors({ unknownFormat: true });
        return;
      }

      this.discussionGuideFile.setErrors(null);
      this.discussionGuideFile.setValue(file);

      this.uploadDiscussionGuide();
    }
  }

  startManual() {
    this.baseDiscussionGuideInput = 'manual';
  }

  private uploadDiscussionGuide() {
    const discussionGuide = this.discussionGuideFile.value;

    if (!discussionGuide || this.discussionGuideFile.invalid) {
      return;
    }

    this.processingDiscussionGuide = true;

    const discussionGuideId = crypto.randomUUID();

    this.dataCaptureService
      .uploadDiscussionGuide(this.sheetId)
      .pipe(
        switchMap((res) =>
          this.dataCaptureService
            .uploadFile(discussionGuide, res.uploadUrl)
            .pipe(map(() => res.fileKey))
        ),
        switchMap((fileKey) =>
          this.dataCaptureService.createDiscussionGuide({
            discussionGuideId,
            discussionGuideKey: fileKey,
            sheetId: this.sheetId,
          })
        ),
        switchMap(() => this.pollDiscussionGuideJob(discussionGuideId)),
        catchError((err) => {
          this.processingDiscussionGuide = false;

          console.error(
            'Error uploading discussion guide or polling for job status',
            err
          );
          this.toastService.error(
            'Please try again or contact your project manager if this persists',
            'Error uploading discussion guide'
          );

          return [];
        })
      )
      .subscribe();
  }

  private pollDiscussionGuideJob(discussionGuideId: string) {
    return timer(5000, 5000).pipe(
      switchMap(() =>
        this.dataCaptureService.pollDiscussionGuideStatus(discussionGuideId)
      ),
      filter(
        (job) =>
          job &&
          [
            DiscussionGuideUploadJobStatus.Complete,
            DiscussionGuideUploadJobStatus.Failed,
          ].includes(job.status)
      ),
      take(1),
      tap((job) => {
        if (job.status === DiscussionGuideUploadJobStatus.Failed) {
          this.toastService.error(
            'There was an error processing the discussion guide. Please ensure the document is formatted correctly and try again.',
            'Error uploading discussion guide'
          );
          this.processingDiscussionGuide = false;
          return;
        }

        this.populateThemeForm(job.discussion_guide_questions);

        this.processingDiscussionGuide = false;
        this.baseDiscussionGuideInput = 'manual';
      }),
      takeUntil(this.destroy$)
    );
  }

  private setDefaults() {
    this.sheetName.setValue(this.sheetDefaults.name || '');

    if (this.sheetDefaults.answer_format) {
      this.answerConfigFormGroup.controls.answerType.setValue(
        this.sheetDefaults.answer_format
      );
    }

    const {
      expert_name,
      expert_bio,
      date_and_time_of_call,
      geography,
      profile_type,
      interviewer_name,
      notetaker_name,
      respondent_source,
    } = this.sheetDefaults.additional_export_headers || {};

    this.additionalFieldsFormGroup.patchValue({
      ...(expert_name !== undefined && { expertName: expert_name }),
      ...(expert_bio !== undefined && { expertBio: expert_bio }),
      ...(date_and_time_of_call !== undefined && {
        dateAndTimeOfCall: date_and_time_of_call,
      }),
      ...(geography !== undefined && { geography }),
      ...(profile_type !== undefined && { profileType: profile_type }),
      ...(interviewer_name !== undefined && {
        interviewerName: interviewer_name,
      }),
      ...(notetaker_name !== undefined && { notetakerName: notetaker_name }),
      ...(respondent_source !== undefined && {
        recruitmentSource: respondent_source,
      }),
      ...(this.sheetDefaults.suggested_question_threshold !== undefined && {
        suggestedQuestionThreshold:
          this.sheetDefaults.suggested_question_threshold,
      }),
    });

    if (this.sheetDefaults.discussion_guide) {
      this.populateThemeForm(this.sheetDefaults.discussion_guide);
    }
  }

  private populateThemeForm(questions: IDiscussionGuideQuestion[]) {
    const themes = this.groupQuestionsByTheme(questions);

    this.themesControl.clear();

    themes.forEach((theme) => {
      const themeGroup = new FormGroup({
        theme: new FormControl<string>(theme.theme),
        questions: new FormArray<FormGroupQuestion>(
          theme.questions
            .filter((q) => !q.parent_question_id)
            .map(
              (question) =>
                new FormGroup({
                  question_id: new FormControl<string>(question.question_id),
                  question: new FormControl<string>(question.question),
                  theme: new FormControl<string>(theme.theme),
                  label: new FormControl<string>(question.label),
                  parent_question_id: new FormControl<string | null>(null),
                  children:
                    this.questionHierarchyService.recursiveFindAndGroupChildren(
                      theme.questions,
                      question.question_id
                    ),
                })
            )
        ),
      });

      this.themesControl.push(themeGroup);
    });
  }

  private groupQuestionsByTheme(discussion_guide: IDiscussionGuideQuestion[]) {
    const themes = discussion_guide.map((question) => question.theme);

    return [...new Set(themes)].map((theme) => ({
      theme,
      questions: discussion_guide.filter(
        (question) => question.theme === theme
      ),
    }));
  }
}
